import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import type { ConsumptionBySector } from '../../../api/wattelzce-demo';
import { useI18n } from '../../../i18n';

interface ConsumptionBySectorChartProps {
  data: ConsumptionBySector[];
  loading?: boolean;
  metric?: 'energy' | 'cost' | 'co2';
}

function ConsumptionBySectorChart({ data, loading, metric = 'energy' }: ConsumptionBySectorChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t, locale } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const metricConfig = {
    energy: {
      title: t('consumptionBySector'),
      subtitle: t('consumptionBySectorSubtitle'),
      unit: 'kWh',
      color: '#059669',
      getValue: (d: ConsumptionBySector) => d.energy,
    },
    cost: {
      title: t('costBySector'),
      subtitle: t('costBySectorSubtitle'),
      unit: '€',
      color: '#2563eb',
      getValue: (d: ConsumptionBySector) => d.cost,
    },
    co2: {
      title: t('co2BySector'),
      subtitle: t('co2BySectorSubtitle'),
      unit: 'kg CO₂',
      color: '#dc2626',
      getValue: (d: ConsumptionBySector) => d.co2,
    },
  };

  const config = metricConfig[metric];
  const sortedData = [...data].sort((a, b) => config.getValue(b) - config.getValue(a));

  const colors = [
    '#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0',
    '#047857', '#065f46', '#064e3b', '#0d9488', '#14b8a6',
  ];

  // Use simplified zone labels for cleaner executive view
  const chartData = sortedData.map((d, index) => ({
    y: config.getValue(d),
    name: d.zoneLabel || t('leg2_zone_n', { n: index + 1 }),
    fullName: d.sector, // Original name for tooltip
    color: colors[index % colors.length],
  }));

  const categories = sortedData.map((d, index) => d.zoneLabel || t('leg2_zone_n', { n: index + 1 }));

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: 320,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: config.title,
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: config.subtitle,
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          fontSize: '11px',
          color: '#374151',
        },
      },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      min: 0,
      title: {
        text: config.unit,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: '{value:,.0f}',
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      useHTML: true,
      formatter: function() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx = this as any;
        const point = ctx.point?.options || {};
        return `
          <div style="padding: 4px 8px;">
            <strong>${point.name || ctx.x}</strong><br/>
            <span style="color: #6b7280;">${point.fullName || ''}</span><br/>
            <span style="color: ${config.color}; font-weight: 600;">${ctx.y?.toLocaleString(locale, {maximumFractionDigits: 1})} ${config.unit}</span>
          </div>
        `;
      },
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderRadius: 8,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: '{point.y:,.0f}',
          style: {
            fontSize: '10px',
            fontWeight: '500',
            color: '#374151',
          },
        },
      },
    },
    series: [{
      type: 'bar',
      name: config.unit,
      data: chartData,
      colorByPoint: true,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
    </div>
  );
}

export default ConsumptionBySectorChart;
